// "Download all photos" -- one component, two renderings, because the two shared
// surfaces have different room for it and identical rules about what it says.
//
//   variant="button" is for a CardHeader's `extraActions` (the shared ENTRY
//     page, whose Photos card has a header of its own).
//   variant="link" is for the entries page, where the photos live inside the
//     table's continuation rows and the card header belongs to the TRIP -- so
//     the control sits beside the view switches instead.
//
// IT STATES THE COST BEFORE THE CLICK. The shared payload carries `size` for
// every photo, so the count and the total are known without asking anything, and
// a 96-photo 400 MB download should never be a surprise. formatBytes comes from
// AttachmentImage, which already exports it; a second byte formatter in this
// codebase would be one too many.
//
// IT ALSO SAYS WHAT IT CANNOT INCLUDE. The Lambda presigns at most
// MAX_SHARED_ENTRY_PHOTOS per day and MAX_SHARED_PHOTOS per trip, so a big trip's
// "all" is genuinely not all -- and a zip that quietly holds 240 of 271 photos is
// worse than one that says so. `withheld` is computed once, on the page that owns
// the payload, so both surfaces report the same number.
//
// Progress is per FILE, not per byte: the fetches are sequential (see
// photoZip.mjs on why that is the memory bound), so "12 of 96" is both accurate
// and all the resolution there is. Cancel and unmount share one AbortController
// -- a 96-photo zip the visitor navigated away from must not keep downloading.

import { useState, useEffect, useRef, useCallback } from 'react';
import Button from 'react-bootstrap/Button';
import ProgressBar from 'react-bootstrap/ProgressBar';
import { formatBytes } from '../../components/AttachmentImage';
import {
  downloadPhotosAsZip,
  zipBytesTotal,
  zipPhotoCount,
} from '../../utilities/photoZip.mjs';

export function DownloadAllPhotos({ groups, zipName, withheld = 0, variant = 'button' }) {
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const abortRef = useRef(null);

  // Unmount is a cancel. Without this, navigating away mid-zip leaves the fetch
  // loop running against a component nobody can see.
  useEffect(() => () => abortRef.current?.abort(), []);

  const count = zipPhotoCount(groups);
  const bytes = zipBytesTotal(groups);

  const start = useCallback(async () => {
    const controller = new AbortController();
    abortRef.current = controller;
    setError(null);
    setNotice(null);
    setProgress({ done: 0, total: count });

    try {
      const result = await downloadPhotosAsZip(groups, {
        zipName,
        signal: controller.signal,
        onProgress: setProgress,
      });
      if (result.stoppedAtCap) {
        setNotice(
          `This download hit its size limit — ${result.packed} of ${count} photos are in the file.`
        );
      }
    } catch (err) {
      // A cancel is not a failure; the visitor already knows what they did.
      if (err?.name !== 'AbortError') setError(err);
    } finally {
      abortRef.current = null;
      setProgress(null);
    }
  }, [groups, zipName, count]);

  if (count === 0) return null;

  const running = progress !== null;

  const withheldNote =
    withheld > 0
      ? `${withheld} more ${withheld === 1 ? "isn't" : "aren't"} included.`
      : null;

  // While it runs the button is Cancel and the count sits BESIDE it, rather than
  // one control labelled "Zipping 12 of 96... Cancel" -- that reads as a single
  // confusing accessible name, and the progress is not what you are clicking.
  const button = running ? (
    <span className="d-inline-flex align-items-center gap-2">
      <span className="text-body-secondary">
        <i className="fa fa-spinner fa-spin me-2" aria-hidden="true"></i>
        Zipping {progress.done} of {progress.total}...
      </span>
      <Button
        variant="outline-secondary"
        size="sm"
        onClick={() => abortRef.current?.abort()}
      >
        Cancel
      </Button>
    </span>
  ) : (
    <Button
      variant={variant === 'link' ? 'link' : 'outline-secondary'}
      size="sm"
      className={variant === 'link' ? 'p-0 align-baseline' : undefined}
      onClick={start}
    >
      <i className="fa-solid fa-file-zipper me-2" aria-hidden="true"></i>
      Download all photos ({count}, {formatBytes(bytes)})
    </Button>
  );

  // The header variant has no room for prose, so its caveats go on the title
  // attribute and the page prints the withheld sentence beside the strip.
  if (variant === 'button') {
    return (
      <span className="d-inline-flex flex-column align-items-end">
        {button}
        {running && (
          <ProgressBar
            className="mt-1 w-100"
            style={{ height: 4 }}
            now={progress.total ? (progress.done / progress.total) * 100 : 0}
          />
        )}
        {(error || notice) && (
          <span className="small text-body-secondary mt-1 text-end">
            {error ? error.message : notice}
          </span>
        )}
      </span>
    );
  }

  return (
    <div className="text-center small">
      {button}
      {withheldNote && !running && (
        <span className="text-body-secondary ms-2">{withheldNote}</span>
      )}
      {running && (
        <ProgressBar
          className="mt-2 mx-auto"
          style={{ height: 4, maxWidth: 320 }}
          now={progress.total ? (progress.done / progress.total) * 100 : 0}
        />
      )}
      {(error || notice) && (
        <p className="text-body-secondary mt-2 mb-0">{error ? error.message : notice}</p>
      )}
    </div>
  );
}

export default DownloadAllPhotos;
